import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  Bell, 
  Lock, 
  Edit3, 
  Clock, 
  Save, 
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  CreditCard,
  Users,
  Plus,
  Trash2,
  Calendar,
  DollarSign,
  Settings,
  Shield,
  Mail,
  UserPlus,
  UserMinus,
  Building,
  Crown,
  Calculator,
  Gift,
  Sparkles,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { billingService } from '../services/billingService';
import SignatureSettingsTab from '../components/SignatureSettingsTab';
import ProfileCompletionProgress from '../components/ProfileCompletionProgress';
import { API_URL } from '../utils/apiConfig';

const SettingsPage = () => {
  const { user, updateProfile, completeOnboardingStep, refreshUserData } = useAuth();
  const { startOnboarding, resetOnboarding } = useOnboarding();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('tab') || 'profile';
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    jobTitle: user?.jobTitle || '',
    companyName: user?.companyName || '',
    phoneNumber: user?.phoneNumber || '',
    useCase: user?.useCase || '',
    userPreferences: {
      emailNotifications: user?.userPreferences?.emailNotifications ?? true,
      signatureStyle: user?.userPreferences?.signatureStyle || 'draw',
      documentSharing: user?.userPreferences?.documentSharing || 'private',
      timeZone: user?.userPreferences?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    }
  });

  // Billing state
  const [teamMembers, setTeamMembers] = useState([]);
  const [portalLoading, setPortalLoading] = useState(false);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [annualLoading, setAnnualLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [role, setRole] = useState('admin');
  const [adminEmail, setAdminEmail] = useState('');
  const [customPricing, setCustomPricing] = useState(null);
  const [promoInfo, setPromoInfo] = useState(null);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [showAddMemberInput, setShowAddMemberInput] = useState(false);

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      setFormData({
        jobTitle: user.jobTitle || '',
        companyName: user.companyName || '',
        phoneNumber: user.phoneNumber || '',
        useCase: user.useCase || '',
        userPreferences: {
          emailNotifications: user.userPreferences?.emailNotifications ?? true,
          signatureStyle: user.userPreferences?.signatureStyle || 'draw',
          documentSharing: user.userPreferences?.documentSharing || 'private',
          timeZone: user.userPreferences?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        }
      });
    }
  }, [user]);

  // Refresh user data on mount to ensure fresh data
  useEffect(() => {
    const refreshData = async () => {
      try {
        await refreshUserData();
      } catch (error) {
        console.error('Failed to refresh user data:', error);
        // Don't show error toast as this is a background refresh
      }
    };

    if (user) {
      refreshData();
    }
  }, []); // Only run on mount

  // Fetch subscription and custom pricing on mount or after checkout success
  useEffect(() => {
    const fetchSub = async () => {
      try {
        const resp = await billingService.getSubscription();
        setSubscription(resp.subscription);
        if (resp.role) setRole(resp.role);
        if (resp.adminEmail) setAdminEmail(resp.adminEmail);

        // Fetch custom pricing for the current user
        try {
          const pricing = await billingService.getCustomPricing(1);
          setCustomPricing(pricing);
        } catch (pricingError) {
          console.error('Failed to fetch custom pricing:', pricingError);
        }

        // Fetch promo information for display
        try {
          const promo = await billingService.checkPromoAvailability();
          setPromoInfo(promo);
        } catch (promoError) {
          console.error('Failed to fetch promo info:', promoError);
        }
        if (Array.isArray(resp.members)) {
          // Filter out admin members since they're displayed separately
          const nonAdminMembers = resp.members.filter(m => !m.isAdmin);
          setTeamMembers(nonAdminMembers.map(m => ({ email: m.email || '' })));
        }
      } catch (err) {
        console.error('Fetch subscription error:', err);
        const status = err?.response?.status;
        const code = err?.response?.data?.error;
        const message = err?.response?.data?.message;
        
        if (status === 409 && code === 'INVITED_MEMBER') {
          // Explicit message for invited users
          toast.error(message || 'You have a pending workspace invite. Please accept it before configuring billing.');
        } else if (status === 401) {
          toast.error('Authentication expired. Please log in again.');
        } else if (status >= 500) {
          toast.error('Server error loading billing information. Please try again.');
        } else {
          console.warn('Subscription data not available, but user may have active payment status');
          // Don't show error for users with active payment status
          if (user?.paymentStatus !== 'active') {
            toast.error('Unable to load subscription details.');
          }
        }
      }
    };

    // if redirected with session=success param, fetch again
    fetchSub();
  }, []);

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'preferences', label: 'Preferences', icon: Bell },
    { id: 'signature', label: 'Signature', icon: Edit3 },
    { id: 'onboarding', label: 'Onboarding', icon: CheckCircle },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'billing', label: 'Billing', icon: CreditCard }
  ];

  const useCaseOptions = [
    { value: 'Legal', label: 'Legal & Compliance' },
    { value: 'HR', label: 'Human Resources' },
    { value: 'Sales', label: 'Sales & Business Development' },
    { value: 'Personal', label: 'Personal Use' },
    { value: 'Other', label: 'Other' }
  ];

  const signatureOptions = [
    { value: 'draw', label: 'Draw signature' },
    { value: 'type', label: 'Type signature' },
    { value: 'upload', label: 'Upload image' }
  ];

  const sharingOptions = [
    { value: 'private', label: 'Private (Recommended)' },
    { value: 'team', label: 'Team accessible' },
    { value: 'public', label: 'Public within organization' }
  ];

  const timeZones = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
    { value: 'America/Chicago', label: 'Central Time (CST/CDT)' },
    { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' }
  ];

  // Track changes
  useEffect(() => {
    const hasProfileChanges = 
      formData.jobTitle !== (user?.jobTitle || '') ||
      formData.companyName !== (user?.companyName || '') ||
      formData.phoneNumber !== (user?.phoneNumber || '') ||
      formData.useCase !== (user?.useCase || '');
      
    const hasPreferenceChanges = 
      formData.userPreferences.emailNotifications !== (user?.userPreferences?.emailNotifications ?? true) ||
      formData.userPreferences.signatureStyle !== (user?.userPreferences?.signatureStyle || 'draw') ||
      formData.userPreferences.documentSharing !== (user?.userPreferences?.documentSharing || 'private') ||
      formData.userPreferences.timeZone !== (user?.userPreferences?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
      
    setHasChanges(hasProfileChanges || hasPreferenceChanges);
  }, [formData, user]);

  const handleInputChange = (field, value, isPreference = false) => {
    if (isPreference) {
      setFormData(prev => ({
        ...prev,
        userPreferences: {
          ...prev.userPreferences,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await updateProfile(formData);
      toast.success('Settings saved successfully!');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to count non-empty member emails
  const getMemberCount = () => teamMembers.filter(m => m.email?.trim()).length + 1; // +1 for admin (filtered out of teamMembers for display)

  // Handle adding a new member with immediate Stripe integration
  const handleAddMember = async () => {
    try {
      const email = newMemberEmail.trim();
      
      if (!email) {
        toast.error('Please enter an email address');
        return;
      }

      if (!email.includes('@') || !email.includes('.')) {
        toast.error('Please enter a valid email address');
        return;
      }

      // Check if email already exists
      if (teamMembers.some(member => member.email?.toLowerCase() === email.toLowerCase())) {
        toast.error('This email is already added');
        return;
      }

      // Add member to UI first
      const newMembers = [...teamMembers, { email }];
      setTeamMembers(newMembers);
      
      // Calculate new count (including admin)
      const newCount = newMembers.filter(m => m.email?.trim()).length + 1;
      
      // Check subscription and update Stripe if needed
      let sub = subscription;
      if (!sub) {
        console.log('🔄 [FRONTEND] Fetching subscription info...');
        const resp = await billingService.getSubscription();
        sub = resp.subscription;
        if (sub) setSubscription(sub);
      }

      if (!sub) {
        toast.error('You need an active subscription before adding team members');
        // Remove the member we just added
        setTeamMembers(teamMembers);
        return;
      }

      // If we need more seats, confirm and update billing
      if (newCount > sub.quantity) {
        const extra = newCount - sub.quantity;
        const extraCost = ((sub.price/100) * extra).toFixed(2);
        const confirmed = window.confirm(`Adding this member will require ${extra} additional seat${extra>1?'s':''} and increase your monthly billing by $${extraCost}. Continue?`);
        
        if (!confirmed) {
          // Remove the member we just added
          setTeamMembers(teamMembers);
          setShowAddMemberInput(false);
          setNewMemberEmail('');
          return;
        }

        // Update Stripe subscription
        const loadingToast = toast.loading('Adding member and updating billing...');

        try {
          const allMemberEmails = newMembers
            .map(m => m.email)
            .filter(Boolean)
            .filter(email => email.trim());

          const response = await billingService.updateSeatCount(newCount, allMemberEmails);
          console.log('✅ [FRONTEND] Backend response:', response);

          toast.dismiss(loadingToast);

          if (response.success) {
            const results = response.invitationResults;
            
            if (results && results.totalEmails > 0) {
              if (results.emailsFailed === 0) {
                toast.success(`✅ Member added successfully!\n📧 Invitation email sent to ${email}`);
              } else {
                toast(`⚠️ Member added but invitation email failed to send. Please try again later.`, {
                  icon: '⚠️',
                  style: {
                    background: '#FEF3C7',
                    color: '#92400E',
                    border: '1px solid #F59E0B'
                  }
                });
              }
            } else {
              toast.success(response.message || 'Member added successfully!');
            }

            // Refresh subscription data
            try {
              const updatedSubscription = await billingService.getSubscription();
              if (updatedSubscription.subscription) {
                setSubscription(updatedSubscription.subscription);
              }
            } catch (refreshError) {
              console.warn('⚠️ [FRONTEND] Failed to refresh subscription data:', refreshError);
            }

          } else {
            toast.error('Failed to add member: ' + (response.error || 'Unknown error'));
            // Remove the member we just added
            setTeamMembers(teamMembers);
          }

        } catch (updateError) {
          toast.dismiss(loadingToast);
          console.error('❌ [FRONTEND] Update failed:', updateError);
          const errorData = updateError?.response?.data;
          if (errorData?.error) {
            toast.error(errorData.error);
          } else {
            toast.error('Network error: Please check your connection and try again');
          }
          // Remove the member we just added
          setTeamMembers(teamMembers);
          return;
        }
      } else {
        // We have enough seats, just send the invitation
        toast.success(`Member ${email} added! They will receive an invitation email.`);
      }

      // Reset form
      setShowAddMemberInput(false);
      setNewMemberEmail('');

    } catch (err) {
      console.error('❌ [FRONTEND] Add member error:', err);
      toast.error('Unexpected error adding member. Please try again.');
      // Remove the member we just added
      setTeamMembers(teamMembers);
    }
  };

  const renderProfileTab = () => (
    <div className="space-y-6">
      {/* Profile Completion Progress */}
      <ProfileCompletionProgress user={user} />
      
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <User className="w-5 h-5 mr-2 text-blue-500" />
          Profile Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name
            </label>
            <input
              type="text"
              value={user?.name || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              {user?.authMethod === 'google' 
                ? 'Name is managed by your Google account'
                : 'Name cannot be edited in settings'
              }
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              {user?.authMethod === 'google' 
                ? 'Email is managed by your Google account'
                : 'Email cannot be changed after account creation'
              }
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Title *
            </label>
            <input
              type="text"
              value={formData.jobTitle}
              onChange={(e) => handleInputChange('jobTitle', e.target.value)}
              placeholder="e.g., Senior Legal Counsel"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name *
            </label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              placeholder="e.g., Acme Corp"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              placeholder="e.g., +1 (555) 123-4567"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Use Case *
            </label>
            <select
              value={formData.useCase}
              onChange={(e) => handleInputChange('useCase', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select use case</option>
              {useCaseOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPreferencesTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Bell className="w-5 h-5 mr-2 text-green-500" />
          Notification Preferences
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">Email Notifications</label>
              <p className="text-sm text-gray-500">Receive updates about your documents and signatures</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.userPreferences.emailNotifications}
                onChange={(e) => handleInputChange('emailNotifications', e.target.checked, true)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Edit3 className="w-5 h-5 mr-2 text-purple-500" />
          Signature Preferences
        </h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Default Signature Style
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {signatureOptions.map((option) => (
              <label
                key={option.value}
                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  formData.userPreferences.signatureStyle === option.value
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="signatureStyle"
                  value={option.value}
                  checked={formData.userPreferences.signatureStyle === option.value}
                  onChange={(e) => handleInputChange('signatureStyle', e.target.value, true)}
                  className="sr-only"
                />
                <div className="text-sm font-medium text-gray-900">{option.label}</div>
                {formData.userPreferences.signatureStyle === option.value && (
                  <CheckCircle className="w-4 h-4 text-purple-500 ml-auto" />
                )}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Lock className="w-5 h-5 mr-2 text-orange-500" />
          Document Sharing
        </h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Default Privacy Level
          </label>
          <div className="space-y-3">
            {sharingOptions.map((option) => (
              <label
                key={option.value}
                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  formData.userPreferences.documentSharing === option.value
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="documentSharing"
                  value={option.value}
                  checked={formData.userPreferences.documentSharing === option.value}
                  onChange={(e) => handleInputChange('documentSharing', e.target.value, true)}
                  className="sr-only"
                />
                <div className="text-sm font-medium text-gray-900">{option.label}</div>
                {formData.userPreferences.documentSharing === option.value && (
                  <CheckCircle className="w-4 h-4 text-orange-500 ml-auto" />
                )}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Clock className="w-5 h-5 mr-2 text-blue-500" />
          Time Zone
        </h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Time Zone
          </label>
          <select
            value={formData.userPreferences.timeZone}
            onChange={(e) => handleInputChange('timeZone', e.target.value, true)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {timeZones.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  const renderOnboardingTab = () => {
    const handleRerunOnboarding = async () => {
      try {
        setIsLoading(true);
        // Reset onboarding in backend
        const response = await fetch(`${API_URL}/users/reset-onboarding`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          // Reset local onboarding state
          resetOnboarding();
          // Start onboarding flow
          startOnboarding();
          toast.success('Onboarding reset! Redirecting to onboarding flow...');
          navigate('/dashboard');
        } else {
          throw new Error('Failed to reset onboarding');
        }
      } catch (error) {
        console.error('Error resetting onboarding:', error);
        toast.error('Failed to reset onboarding. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
            Onboarding Status
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <div>
                  <h4 className="text-sm font-medium text-green-800">Onboarding Complete</h4>
                  <p className="text-sm text-green-600">
                    You've successfully completed the onboarding process
                  </p>
                </div>
              </div>
              <div className="text-green-600">
                <CheckCircle className="w-8 h-8" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Profile Information</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <p><span className="font-medium">Company:</span> {user?.companyName || 'Not provided'}</p>
                  <p><span className="font-medium">Job Title:</span> {user?.jobTitle || 'Not provided'}</p>
                  <p><span className="font-medium">Use Case:</span> {user?.useCase || 'Not provided'}</p>
                  <p><span className="font-medium">Phone:</span> {user?.phoneNumber || 'Not provided'}</p>
                </div>
              </div>

              <div className="p-4 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Preferences</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <p><span className="font-medium">Email Notifications:</span> {user?.userPreferences?.emailNotifications ? 'Enabled' : 'Disabled'}</p>
                  <p><span className="font-medium">Signature Style:</span> {user?.userPreferences?.signatureStyle || 'draw'}</p>
                  <p><span className="font-medium">Document Sharing:</span> {user?.userPreferences?.documentSharing || 'private'}</p>
                  <p><span className="font-medium">Time Zone:</span> {user?.userPreferences?.timeZone || 'UTC'}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Onboarding Steps Completed</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {Object.entries(user?.onboardingSteps || {}).map(([step, completed]) => (
                  <div key={step} className={`p-2 rounded text-center text-xs ${
                    completed 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-gray-100 text-gray-600 border border-gray-200'
                  }`}>
                    <div className="flex items-center justify-center mb-1">
                      {completed ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : (
                        <div className="w-3 h-3 rounded-full border border-gray-400"></div>
                      )}
                    </div>
                    <div className="capitalize font-medium">
                      {step.replace(/([A-Z])/g, ' $1').trim()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-orange-200 rounded-lg bg-orange-50">
              <div>
                <h4 className="text-sm font-medium text-orange-800">Re-run Onboarding</h4>
                <p className="text-sm text-orange-600">
                  Start the onboarding process again to update your information or experience the flow
                </p>
              </div>
              <button
                onClick={handleRerunOnboarding}
                disabled={isLoading}
                className="flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{isLoading ? 'Resetting...' : 'Re-run Onboarding'}</span>
              </button>
            </div>

            <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
              <h4 className="text-sm font-medium text-blue-800">Debug Information</h4>
              <div className="mt-2 text-xs text-blue-700">
                <p><span className="font-medium">User ID:</span> {user?.id}</p>
                <p><span className="font-medium">Onboarding Completed:</span> {user?.onboardingCompleted ? 'true' : 'false'}</p>
                <p><span className="font-medium">Created:</span> {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}</p>
                <p><span className="font-medium">Last Updated:</span> {user?.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : 'Unknown'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSecurityTab = () => {
    // Determine authentication method based on backend response
    const isGoogleAccount = user?.authMethod === 'google';
    const isHybridAccount = user?.authMethod === 'hybrid';
    const isEmailAccount = user?.authMethod === 'email';
    
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Lock className="w-5 h-5 mr-2 text-red-500" />
            Account Security
          </h3>
          
          <div className="space-y-4">
            {isGoogleAccount ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Google Account Security</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Your account security is managed by Google. To change your password or enable two-factor authentication, 
                      please visit your Google Account settings.
                    </p>
                    <a
                      href="https://myaccount.google.com/security"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 text-sm text-yellow-800 hover:text-yellow-900 underline"
                    >
                      Manage Google Account Security →
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <Lock className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-800">
                      {isHybridAccount ? 'Hybrid Account' : 'Email/Password Account'}
                    </h4>
                    <p className="text-sm text-blue-700 mt-1">
                      {isHybridAccount 
                        ? 'Your account supports both email/password and Google authentication methods.'
                        : 'Your account uses email and password authentication. Contact support if you need to reset your password or enable additional security features.'
                      }
                    </p>
                    <div className="mt-3 space-y-2">
                      <div className="text-sm text-blue-700">
                        <span className="font-medium">Email:</span> {user?.email}
                      </div>
                      <div className="text-sm text-blue-700">
                        <span className="font-medium">Authentication:</span> 
                        {isHybridAccount ? ' Email & Password + Google OAuth' : ' Email & Password'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Connected Account</h4>
            <div className="flex items-center space-x-3">
              <img
                src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=3b82f6&color=fff`}
                alt="Profile"
                className="w-10 h-10 rounded-full"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  };

  const renderBillingTab = () => {
    if (role !== 'admin') {
      const handleLeaveWorkspace = async () => {
        const confirmed = window.confirm(
          'Are you sure you want to leave this workspace? You will lose access to all shared documents and need a new invitation to rejoin.'
        );
        
        if (!confirmed) return;

        try {
          const response = await fetch(`${API_URL}/workspace/leave`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error('Failed to leave workspace');
          }

          const data = await response.json();
          
          // Provide enhanced feedback based on whether they have a fallback workspace
          if (data.newDefaultWorkspaceId) {
            toast.success(
              `${data.message} You've been returned to your previous workspace.`,
              { duration: 5000 }
            );
          } else {
            toast.success(
              `${data.message} You can now join other workspaces or continue using the platform individually.`,
              { duration: 5000 }
            );
          }
          
          // Refresh user data to update UI
          await refreshUserData();
        } catch (error) {
          console.error('Error leaving workspace:', error);
          toast.error('Failed to leave workspace. Please try again.');
        }
      };

      return (
        <div className="max-w-6xl mx-auto space-y-8">
          
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Workspace Membership</h1>
            <p className="text-gray-600">You are a member of a team workspace</p>
          </div>

          {/* Workspace Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <Building className="w-6 h-6 mr-2 text-blue-500" />
                Workspace Details
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Workspace Info */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Your Role</label>
                  <div className="mt-1">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 capitalize">
                      <Users className="w-4 h-4 mr-1" />
                      {role}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Workspace Administrator</label>
                  <div className="mt-1 flex items-center">
                    <Crown className="w-4 h-4 text-yellow-500 mr-2" />
                    <span className="text-gray-900 font-medium">{adminEmail}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Contact the administrator for billing questions, seat changes, or workspace management.
                  </p>
                </div>
              </div>

              {/* Subscription Info (Limited for members) */}
              <div className="space-y-4">
                {subscription && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Team Size</label>
                      <div className="mt-1">
                        <span className="text-2xl font-bold text-gray-900">{subscription.quantity}</span>
                        <span className="text-sm text-gray-500 ml-1">total seats</span>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">Plan Status</label>
                      <div className="mt-1">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {subscription.status === 'active' ? 'Active' : subscription.status}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Member Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Settings className="w-6 h-6 mr-2 text-gray-500" />
              Workspace Actions
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Contact Administrator</h4>
                  <p className="text-sm text-gray-600">Need help with billing, permissions, or workspace settings?</p>
                </div>
                <a
                  href={`mailto:${adminEmail}?subject=Workspace Support Request`}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </a>
              </div>

              <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                <div>
                  <h4 className="font-medium text-red-900">Leave Workspace</h4>
                  <p className="text-sm text-red-700">
                    This will remove your access to all shared documents and the workspace. 
                    You'll need a new invitation to rejoin.
                  </p>
                </div>
                <button
                  onClick={handleLeaveWorkspace}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <UserMinus className="w-4 h-4 mr-2" />
                  Leave Workspace
                </button>
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-8">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Need Help?</h3>
              <p className="text-blue-700 mb-4">
                As a workspace member, your administrator manages billing and team settings. 
                For immediate assistance with your account or documents, contact your workspace administrator.
              </p>

            </div>
          </div>

        </div>
      );
    }

    // Handle the new pricing format for standard tier users
    const isStandardTier = customPricing?.pricingTier === 'standard' && customPricing?.useExistingPrice;
    const currentSeats = subscription?.quantity || 0;
    const plannedSeats = getMemberCount();
    
    let pricePerSeat, monthlyCost, plannedCost, billingInterval, displayPrice;
    
    if (isStandardTier) {
      // Standard tier uses $150/year pricing
      displayPrice = 150; // $150 per year
      billingInterval = 'year';
      pricePerSeat = displayPrice; // For standard tier, this is per year
      monthlyCost = displayPrice; // Total cost is $150/year
      plannedCost = displayPrice; // Same for planned cost
    } else {
      // Non-standard tiers use monthly pricing
      pricePerSeat = customPricing?.pricePerSeat || 15; // Fallback to $15/month
      billingInterval = 'month';
      displayPrice = pricePerSeat;
      monthlyCost = currentSeats * pricePerSeat;
      plannedCost = plannedSeats * pricePerSeat;
    }

    // Calculate free trial status
    const freeTrialUsed = user?.freeTrialDocumentsUsed || 0;
    const freeTrialLimit = user?.freeTrialDocumentsLimit || 2;
    const freeTrialRemaining = Math.max(0, freeTrialLimit - freeTrialUsed);



    return (
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Billing & Subscription</h1>
          <p className="text-gray-600">Manage your subscription and billing settings</p>
        </div>

        {/* Plan Comparison for Non-subscribers */}
        {!subscription && user?.paymentStatus !== 'active' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Free Trial Plan */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Free Trial</h3>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FREE
                </span>
              </div>
              
              <div className="mb-6">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {freeTrialRemaining}
                  <span className="text-lg font-medium text-gray-600"> documents left</span>
                </div>
                <div className="text-sm text-gray-500">
                  {freeTrialUsed} of {freeTrialLimit} used
                </div>
              </div>

              <div className={`w-full mb-6 py-3 px-4 rounded-lg font-medium text-center ${
                freeTrialRemaining > 0 
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'bg-orange-50 text-orange-700 border border-orange-200'
              }`}>
                {freeTrialRemaining > 0 ? 'Current Plan' : 'Trial Expired'}
              </div>

              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
                  Up to {freeTrialLimit} documents
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
                  Basic signature tools
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
                  Document storage
                </div>
                <div className="flex items-center text-sm text-gray-400">
                  <X className="w-4 h-4 text-gray-400 mr-3" />
                  Advanced signature tools
                </div>
                <div className="flex items-center text-sm text-gray-400">
                  <X className="w-4 h-4 text-gray-400 mr-3" />
                  Priority support
                </div>
              </div>
            </div>

            {/* Monthly Plan */}
            <div className="bg-gradient-to-b from-blue-50 to-sky-50 rounded-xl shadow-md border-2 border-blue-200 p-6 relative">
              {promoInfo?.available && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-orange-500 text-white shadow-lg">
                    🎉 Limited Time
                  </span>
                </div>
              )}
              
              <div className="flex items-center justify-between mb-4 mt-2">
                <h3 className="text-xl font-semibold text-gray-900">Monthly Plan</h3>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FLEXIBLE
                </span>
              </div>
              
              <div className="mb-6">
                {promoInfo?.available ? (
                  <div className="space-y-2">
                    <div className="text-4xl font-bold text-blue-900">
                      $12.50
                      <span className="text-lg font-medium text-gray-600">/month</span>
                    </div>
                    <div className="text-lg text-gray-500 line-through">$15.00/month</div>
                    <div className="text-sm text-blue-600 font-medium">
                      First 100 users special • Then $15.00/month
                    </div>
                    <div className="text-xs text-gray-500">
                      Billed monthly
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-4xl font-bold text-blue-900">
                      $15.00
                      <span className="text-lg font-medium text-gray-600">/month</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      Billed monthly
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={async () => {
                  try {
                    setMonthlyLoading(true);
                    const memberCount = teamMembers.filter(m => m.email?.trim()).length;
                    const totalSeats = memberCount + 1; // +1 for admin
                    const { url } = await billingService.createCheckoutSession({ 
                      quantity: totalSeats, 
                      billingInterval: 'monthly' 
                    });
                    window.location.href = url;
                  } catch (err) {
                    console.error(err);
                    toast.error('Failed to start subscription');
                  } finally {
                    setMonthlyLoading(false);
                  }
                }}
                disabled={monthlyLoading || annualLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg mb-6"
              >
                {monthlyLoading ? (
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                ) : null}
                {promoInfo?.available ? 'Upgrade - Limited Time!' : 'Get Monthly Plan'}
              </button>

              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
                  Unlimited documents
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
                  Advanced signature tools
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
                  Priority support
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <Clock className="w-4 h-4 text-orange-500 mr-3" />
                  Team collaboration <span className="text-xs text-orange-600 font-medium ml-2">Coming Soon</span>
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <Clock className="w-4 h-4 text-orange-500 mr-3" />
                  Advanced workflows <span className="text-xs text-orange-600 font-medium ml-2">Coming Soon</span>
                </div>
              </div>

              {promoInfo?.available && (
                <div className="mt-4 text-center">
                  <div className="text-xs text-blue-600 font-medium">
                    {promoInfo.remainingUses} limited time spots remaining!
                  </div>
                </div>
              )}
            </div>

            {/* Annual Plan */}
            <div className="bg-gradient-to-b from-purple-50 to-blue-50 rounded-xl shadow-lg border-2 border-purple-200 p-6 relative">
                {promoInfo?.available && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-orange-500 text-white shadow-lg">
                      🎉 Limited Time
                    </span>
                  </div>
                )}
                
                <div className="flex items-center justify-between mb-4 mt-2">
                  <h3 className="text-xl font-semibold text-gray-900">Annual Plan</h3>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    SAVE 20%
                  </span>
                </div>
                
                <div className="mb-6">
                  {promoInfo?.available ? (
                    <div className="space-y-2">
                      <div className="text-4xl font-bold text-purple-900">
                        $9.99
                        <span className="text-lg font-medium text-gray-600">/month</span>
                      </div>
                      <div className="text-lg text-gray-500 line-through">$12.50/month</div>
                      <div className="text-sm text-purple-600 font-medium">
                        First year special • Then $12.50/month
                      </div>
                      <div className="text-xs text-gray-500">
                        Billed annually ($119.88 first year)
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-4xl font-bold text-purple-900">
                        $12.50
                        <span className="text-lg font-medium text-gray-600">/month</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        Billed annually ($150/year)
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={async () => {
                    try {
                      setAnnualLoading(true);
                      const memberCount = teamMembers.filter(m => m.email?.trim()).length;
                      const totalSeats = memberCount + 1; // +1 for admin
                      const { url } = await billingService.createCheckoutSession({ 
                        quantity: totalSeats, 
                        billingInterval: 'yearly' 
                      });
                      window.location.href = url;
                    } catch (err) {
                      console.error(err);
                      toast.error('Failed to start subscription');
                    } finally {
                      setAnnualLoading(false);
                    }
                  }}
                  disabled={monthlyLoading || annualLoading}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl mb-6"
                >
                  {annualLoading ? (
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : (
                    <Crown className="w-5 h-5 mr-2" />
                  )}
                  {promoInfo?.available ? 'Upgrade - Limited Time!' : 'Get Annual Plan'}
                </button>

                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
                    Unlimited documents
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
                    Advanced signature tools
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
                    Priority support
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-4 h-4 text-orange-500 mr-3" />
                    Team collaboration <span className="text-xs text-orange-600 font-medium ml-2">Coming Soon</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-4 h-4 text-orange-500 mr-3" />
                    Advanced workflows <span className="text-xs text-orange-600 font-medium ml-2">Coming Soon</span>
                  </div>
                </div>

                {promoInfo?.available && (
                  <div className="mt-4 text-center">
                    <div className="text-xs text-purple-600 font-medium">
                      {promoInfo.remainingUses} limited time spots remaining!
                    </div>
                  </div>
                )}
              </div>
            </div>
        ) : (
          /* Active Subscriber View */
          <div className="space-y-6">
            {/* Pro Plan Status */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl shadow-lg border-2 border-purple-200 p-8 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-green-500 text-white shadow-lg">
                  ✓ Active Subscription
                </span>
              </div>
              
              <div className="text-center mt-2">
                <div className="flex items-center justify-center mb-4">
                  <Crown className="w-8 h-8 text-purple-600 mr-3" />
                  <h2 className="text-3xl font-bold text-purple-900">Pro Plan</h2>
                </div>
                
                <div className="space-y-4 mb-8">
                  {subscription ? (
                    <>
                      {(() => {
                        // Calculate display pricing - use actual amount paid if available
                        let displayAmount, billingPeriod, perSeatAmount, isDiscounted = false;
                        
                        if (subscription.actual_amount_paid && subscription.interval) {
                          // Use actual amount paid from invoice (includes discounts)
                          const actualTotal = subscription.actual_amount_paid / 100; // Convert from cents
                          isDiscounted = subscription.discount != null;
                          
                          if (subscription.interval === 'year') {
                            displayAmount = (actualTotal / 12).toFixed(2); // Show monthly equivalent
                            billingPeriod = 'month';
                            perSeatAmount = (actualTotal / 12 / subscription.quantity).toFixed(2);
                          } else {
                            displayAmount = actualTotal.toFixed(2);
                            billingPeriod = 'month';
                            perSeatAmount = (actualTotal / subscription.quantity).toFixed(2);
                          }
                        } else {
                          // Fallback to standard pricing calculation
                          displayAmount = ((subscription.price * subscription.quantity) / 100).toFixed(2);
                          billingPeriod = subscription.interval === 'year' ? 'month' : 'month';
                          perSeatAmount = (subscription.price / 100).toFixed(2);
                        }
                        
                        return (
                          <>
                            <div className="text-5xl font-bold text-purple-900">
                              ${displayAmount}
                              <span className="text-xl font-medium text-gray-600">/{billingPeriod}</span>
                            </div>
                            
                            <div className="space-y-2">
                              <div className="text-lg text-gray-700">
                                {subscription.quantity} seat{subscription.quantity !== 1 ? 's' : ''} • ${perSeatAmount} per seat/{billingPeriod}
                              </div>
                              {subscription.interval === 'year' && subscription.actual_amount_paid && (
                                <div className="text-sm text-gray-500">
                                  Annual total: ${(subscription.actual_amount_paid / 100).toFixed(2)}
                                  {isDiscounted && subscription.discount && (
                                    <span className="ml-2 text-green-600 font-medium">
                                      ({subscription.discount.percent_off}% discount applied)
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className="text-sm text-gray-500">
                                Next billing: {new Date(subscription.current_period_end * 1000).toLocaleDateString()}
                              </div>
                              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                {subscription.status === 'active' ? 'Active' : subscription.status}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </>
                  ) : (
                    /* Fallback for active payment status without detailed subscription */
                    <>
                      <div className="text-4xl font-bold text-purple-900">
                        Pro Plan
                      </div>
                      <div className="space-y-4">
                        <div className="text-lg text-gray-700">
                          Subscription details loading...
                        </div>
                        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                          Active
                        </div>
                        <button
                          onClick={() => {
                            // Refresh subscription data
                            const fetchSub = async () => {
                              try {
                                const resp = await billingService.getSubscription();
                                setSubscription(resp.subscription);
                                if (resp.role) setRole(resp.role);
                                if (resp.adminEmail) setAdminEmail(resp.adminEmail);
                                toast.success('Subscription data refreshed');
                              } catch (err) {
                                console.error('Retry fetch error:', err);
                                if (err?.response?.status === 404 || err?.message?.includes('not found')) {
                                  toast.error('Subscription not found. This may be a development/production environment mismatch. Please contact support.');
                                } else {
                                  toast.error('Unable to load subscription details. Please contact support.');
                                }
                              }
                            };
                            fetchSub();
                          }}
                          className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
                        >
                          Refresh Billing Info
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Feature List for Active Plan */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  <div className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    Unlimited documents
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    Advanced signature tools
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    Priority support
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    Multiple team members
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-5 h-5 text-orange-500 mr-3" />
                    Team collaboration <span className="text-xs text-orange-600 font-medium ml-2">Coming Soon</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-5 h-5 text-orange-500 mr-3" />
                    Advanced workflows <span className="text-xs text-orange-600 font-medium ml-2">Coming Soon</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        



        {/* Pricing Information - Only show for paid subscribers */}
        {/* {customPricing && subscription && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-green-900 mb-2 flex items-center">
                  <Crown className="w-5 h-5 mr-2" />
                  Your Pricing Plan
                </h3>
                <p className="text-green-700 mb-3">
                  You're on our <strong>{customPricing.pricingTier.toUpperCase()}</strong> tier
                  {isStandardTier && ' with our standard yearly pricing'}
                  {!isStandardTier && ' with special pricing'}
                </p>
                <div className="flex items-center space-x-4">
                  <div className="bg-white rounded-lg px-3 py-2 border border-green-200">
                    <div className="text-2xl font-bold text-green-900">
                      ${isStandardTier ? displayPrice.toFixed(2) : customPricing.pricePerSeat.toFixed(2)}
                    </div>
                    <div className="text-sm text-green-600">
                      {isStandardTier ? 'per year' : 'per seat/month'}
                    </div>
                  </div>
                  {!isStandardTier && customPricing.discountPercent > 0 && (
                    <div className="bg-green-200 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      {customPricing.discountPercent}% discount
                    </div>
                  )}
                  {isStandardTier && (
                    <div className="bg-blue-200 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      Fixed yearly rate
                    </div>
                  )}
                </div>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </div>
        )} */}

        {/* Subscription Management - Only for Active Subscriptions */}
        {(subscription || user?.paymentStatus === 'active') && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Subscription Management */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="w-6 h-6 mr-2 text-blue-500" />
              Subscription Management
            </h3>
            
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-800">Active Subscription</p>
                      <p className="text-sm text-blue-700">
                        {subscription ? (
                          <>
                            {subscription.quantity} seat{subscription.quantity !== 1 ? 's' : ''} • Next billing: {new Date(subscription.current_period_end*1000).toLocaleDateString()}
                            
                          </>
                        ) : (
                          'Loading subscription details...'
                        )}
                      </p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      setPortalLoading(true);
                      const { url } = await billingService.createBillingPortalSession();
                      window.location.href = url;
                    } catch (error) {
                      console.error('Error opening billing portal:', error);
                      toast.error('Failed to open billing portal');
                    } finally {
                      setPortalLoading(false);
                    }
                  }}
                  disabled={portalLoading || monthlyLoading || annualLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                >
                  {portalLoading ? (
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : (
                    <Settings className="w-5 h-5 mr-2" />
                  )}
                  Manage Subscription & Payment
                </button>
              </div>
          </div>

          {/* Team Members Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <Users className="w-6 h-6 mr-2 text-purple-500" />
                Team Members
              </h3>
              <div className="flex items-center space-x-4">
                {!showAddMemberInput ? (
                  <button
                    onClick={() => setShowAddMemberInput(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white hover:bg-purple-700 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm font-medium">Add Member</span>
                  </button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <input
                      type="email"
                      value={newMemberEmail}
                      onChange={(e) => setNewMemberEmail(e.target.value)}
                      placeholder="member@company.com"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      onKeyPress={async (e) => {
                        if (e.key === 'Enter') {
                          await handleAddMember();
                        }
                      }}
                      autoFocus
                    />
                    <button
                      onClick={async () => {
                        await handleAddMember();
                      }}
                      className="px-3 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg text-sm transition-colors"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setShowAddMemberInput(false);
                        setNewMemberEmail('');
                      }}
                      className="px-3 py-2 bg-gray-300 text-gray-700 hover:bg-gray-400 rounded-lg text-sm transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                <div className="text-right">
                  <div className="text-sm text-gray-500">Total Seats</div>
                  <div className="text-lg font-semibold text-gray-900">{subscription ? subscription.quantity : getMemberCount()}</div>
                </div>
              </div>
            </div>

            {/* Table Header */}
            <div className="grid grid-cols-4 gap-4 pb-3 mb-4 border-b border-gray-200">
              <div className="text-sm font-medium text-gray-500">Name</div>
              <div className="text-sm font-medium text-gray-500">Email</div>
              <div className="text-sm font-medium text-gray-500">Role</div>
              <div className="text-sm font-medium text-gray-500 text-right">Actions</div>
            </div>

            {/* Admin Row */}
            <div className="grid grid-cols-4 gap-4 py-3 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Crown className="w-4 h-4 text-blue-600" />
                </div>
                <div className="text-sm font-medium text-gray-900">{user?.name || 'You'}</div>
              </div>
              <div className="text-sm text-gray-600">{user?.email}</div>
              <div>
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                  Admin
                </span>
              </div>
              <div className="text-right">
                <span className="text-sm text-gray-400">Owner</span>
              </div>
            </div>

            {/* Team Members Rows */}
            {teamMembers.map((member, idx) => (
              <div key={idx} className="grid grid-cols-4 gap-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="text-sm text-gray-900">Team Member</div>
                </div>
                <div className="flex items-center">
                  <input
                    type="email"
                    value={member.email}
                    onChange={(e) => {
                      const newMembers = [...teamMembers];
                      newMembers[idx].email = e.target.value;
                      setTeamMembers(newMembers);
                    }}
                    placeholder="member@company.com"
                    className="text-sm text-gray-600 border-0 bg-transparent focus:ring-0 focus:outline-none w-full"
                  />
                </div>
                <div>
                  <span className="bg-gray-100 text-gray-700 text-xs font-medium px-2 py-1 rounded-full">
                    Member
                  </span>
                </div>
                <div className="text-right">
                  <button
                    onClick={async () => {
                      const memberEmail = member.email?.trim();
                      
                      if (memberEmail) {
                        try {
                          const response = await fetch(`${API_URL}/workspace/remove-member`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`,
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({ memberEmail })
                          });

                          if (!response.ok) {
                            const errorData = await response.json();
                            throw new Error(errorData.error || 'Failed to remove member');
                          }

                          const data = await response.json();
                          toast.success(data.message || 'Member removed successfully');
                        } catch (error) {
                          console.error('Error removing member:', error);
                          toast.error(error.message || 'Failed to remove member');
                          return;
                        }
                      }
                      
                      const newMembers = teamMembers.filter((_, i) => i !== idx);
                      setTeamMembers(newMembers);
                    }}
                    className="text-sm text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1 rounded transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}

            {/* Empty State */}
            {teamMembers.length === 0 && (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <Users className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-sm text-gray-500">No team members yet. Add your first team member to get started.</p>
              </div>
            )}

            {/* Update Button */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Total members: {getMemberCount()} • Subscription seats: {subscription ? subscription.quantity : 'N/A'}
                </div>
                <button
                  onClick={async () => {
                    try {
                      console.log('🔄 [FRONTEND] Starting team member update...');
                      const memberCount = teamMembers.filter(m => m.email?.trim()).length;
                      const count = memberCount + 1; // +1 for admin (filtered out of teamMembers)
                      console.log('🔄 [FRONTEND] Valid team members count:', memberCount, 'Total seats needed:', count);

                      const invalidEmails = teamMembers
                        .filter(m => m.email?.trim())
                        .filter(m => !m.email.includes('@') || !m.email.includes('.'))
                        .map(m => m.email);
                      
                      if (invalidEmails.length > 0) {
                        toast.error(`Invalid email format: ${invalidEmails.join(', ')}`);
                        return;
                      }

                      let sub = subscription;
                      if (!sub) {
                        console.log('🔄 [FRONTEND] Fetching subscription info...');
                        const resp = await billingService.getSubscription();
                        sub = resp.subscription;
                        if (sub) setSubscription(sub);
                      }

                      if (!sub) {
                        toast.error('You need an active subscription before editing seats');
                        return;
                      }

                      console.log('✅ [FRONTEND] Current subscription:', { quantity: sub.quantity, newCount: count });

                      if (sub && count > sub.quantity) {
                        const extra = count - sub.quantity;
                        const extraCost = ((sub.price/100) * extra).toFixed(2);
                        const ok = window.confirm(`This will add ${extra} additional seat${extra>1?'s':''} and increase your monthly billing by $${extraCost}. Continue?`);
                        if (!ok) return;
                      }

                      // Send all member emails - backend will determine which need invitations
                      const allMemberEmails = teamMembers
                        .map(m => m.email)
                        .filter(Boolean)
                        .filter(email => email.trim());
                      
                      console.log('🔄 [FRONTEND] All member emails:', allMemberEmails);

                      const loadingToast = toast.loading('Updating team and billing...');

                      try {
                        const response = await billingService.updateSeatCount(count, allMemberEmails);
                        console.log('✅ [FRONTEND] Backend response:', response);

                        toast.dismiss(loadingToast);

                        if (response.success) {
                          const results = response.invitationResults;
                          
                          if (results && results.totalEmails > 0) {
                            if (results.emailsFailed === 0) {
                              toast.success(`✅ Team updated successfully!\n📧 ${results.emailsSent} invitation emails sent`);
                            } else {
                              toast(`⚠️ Partial success: ${results.membersCreated} members added, but ${results.emailsFailed} email(s) failed to send.`, {
                                icon: '⚠️',
                                style: {
                                  background: '#FEF3C7',
                                  color: '#92400E',
                                  border: '1px solid #F59E0B'
                                }
                              });
                            }
                          } else {
                            toast.success(response.message || 'Seat count updated successfully!');
                          }
                        } else {
                          toast.error('Update failed: ' + (response.error || 'Unknown error'));
                        }

                      } catch (updateError) {
                        toast.dismiss(loadingToast);
                        console.error('❌ [FRONTEND] Update failed:', updateError);
                        const errorData = updateError?.response?.data;
                        if (errorData?.error) {
                          toast.error(errorData.error);
                        } else {
                          toast.error('Network error: Please check your connection and try again');
                        }
                        return;
                      }

                      try {
                        const updatedSubscription = await billingService.getSubscription();
                        if (updatedSubscription.subscription) {
                          setSubscription(updatedSubscription.subscription);
                        }
                      } catch (refreshError) {
                        console.warn('⚠️ [FRONTEND] Failed to refresh subscription data:', refreshError);
                      }

                    } catch (err) {
                      console.error('❌ [FRONTEND] Outer error:', err);
                      toast.error('Unexpected error. Please refresh the page and try again.');
                    }
                  }}
                  disabled={false}
                  className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Update Team & Billing
                </button>
              </div>
            </div>

            {!subscription && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Subscription required:</strong> Start a subscription to manage team members and send invitations.
                </p>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Billing Summary */}
        {(subscription || user?.paymentStatus === 'active') && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Building className="w-6 h-6 mr-2 text-indigo-500" />
              Billing Summary
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Current Plan</span>
                  <span className="font-medium">
                    {subscription ? (
                      `Pro Plan - ${subscription.quantity} seat${subscription.quantity !== 1 ? 's' : ''}`
                    ) : (
                      'Pro Plan - Active'
                    )}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Monthly Equivalent</span>
                  <span className="font-medium">
                    {subscription ? (
                      (() => {
                        if (subscription.actual_amount_paid && subscription.interval === 'year') {
                          const monthlyEquivalent = (subscription.actual_amount_paid / 100 / 12).toFixed(2);
                          return `$${monthlyEquivalent}`;
                        } else if (subscription.actual_amount_paid) {
                          return `$${(subscription.actual_amount_paid / 100).toFixed(2)}`;
                        } else {
                          return `$${((subscription.price * subscription.quantity) / 100).toFixed(2)}`;
                        }
                      })()
                    ) : (
                      'Loading...'
                    )}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-900 font-semibold">
                    {subscription?.interval === 'year' ? 'Annual Total' : 'Monthly Total'}
                  </span>
                  <span className="text-xl font-bold text-gray-900">
                    {subscription ? (
                      (() => {
                        if (subscription.actual_amount_paid) {
                          return `$${(subscription.actual_amount_paid / 100).toFixed(2)}`;
                        } else {
                          return `$${((subscription.price * subscription.quantity) / 100).toFixed(2)}`;
                        }
                      })()
                    ) : (
                      'Loading...'
                    )}
                  </span>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Next Invoice</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    {subscription ? (
                      <>
                        <div>Date: {new Date(subscription.current_period_end*1000).toLocaleDateString()}</div>
                        <div>Amount: ${((subscription.price * subscription.quantity) / 100).toFixed(2)}</div>
                        <div>Billing Period: {subscription.interval === 'year' ? 'Yearly' : 'Monthly'}</div>
                        {subscription.discount && (
                          <div className="text-orange-600 text-xs font-medium mt-1">
                            *Regular price (promotional discount expires)
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div>Date: Loading...</div>
                        <div>Amount: Loading...</div>
                        <div>Billing Period: Loading...</div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileTab();
      case 'preferences':
        return renderPreferencesTab();
      case 'signature':
        return <SignatureSettingsTab />;
      case 'onboarding':
        return renderOnboardingTab();
      case 'security':
        return renderSecurityTab();
      case 'billing':
        return renderBillingTab();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Dashboard
              </button>
              <div className="w-px h-6 bg-gray-300"></div>
              <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            </div>
            
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              {renderTabContent()}
            </motion.div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default SettingsPage;